type CalendarUnitFamily = "hour" | "day" | "week" | "month";

const PT: Record<CalendarUnitFamily, { singular: string; plural: string }> = {
  hour: { singular: "hora", plural: "horas" },
  day: { singular: "dia", plural: "dias" },
  week: { singular: "semana", plural: "semanas" },
  month: { singular: "mês", plural: "meses" },
};

const EN: Record<CalendarUnitFamily, { singular: string; plural: string }> = {
  hour: { singular: "hour", plural: "hours" },
  day: { singular: "day", plural: "days" },
  week: { singular: "week", plural: "weeks" },
  month: { singular: "month", plural: "months" },
};

function stripDiacritics(input: string): string {
  return input.normalize("NFD").replace(/\p{M}/gu, "");
}

function detectCalendarUnitFamily(raw: string): CalendarUnitFamily | null {
  const u = stripDiacritics(raw.trim().toLowerCase());
  if (["hora", "horas", "hour", "hours", "h"].includes(u)) return "hour";
  if (["dia", "dias", "day", "days"].includes(u)) return "day";
  if (["semana", "semanas", "week", "weeks"].includes(u)) return "week";
  if (["mes", "meses", "month", "months"].includes(u)) return "month";
  return null;
}

/** Parses a numeric count from posology fields (supports comma as decimal separator). */
export function parsePosologyCount(raw: string | null | undefined): number | null {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}

export function isSingularCount(n: number): boolean {
  return Math.abs(n - 1) < 1e-9;
}

/** Lowercase measure labels (dose unit) for display. */
export function normalizeDoseMeasureUnit(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Picks singular or plural calendar unit (frequency/duration) from the numeric value.
 * Unknown/custom units are lowercased only.
 */
export function resolveCalendarUnitForCount(
  rawUnit: string,
  countStr: string | null | undefined,
  language: string,
): string {
  const trimmed = rawUnit.trim();
  if (!trimmed) return "";

  const family = detectCalendarUnitFamily(trimmed);
  if (!family) {
    return trimmed.toLowerCase();
  }

  const count = parsePosologyCount(countStr ?? "");
  const usePlural = count == null ? true : !isSingularCount(count);
  const isPt = language.toLowerCase().startsWith("pt");
  const forms = isPt ? PT[family] : EN[family];
  return usePlural ? forms.plural : forms.singular;
}
